import React, { useState, useEffect, useRef } from 'react';
import { Row, Col, InputGroup, Form } from 'react-bootstrap';
import NoticeTile from './NoticeTile';
import BeneficiaryTile from './BeneficiaryTile';
import { dateFormatter, formatToDecimals } from '@lib/helpers/formatting';
import { Loading } from '@components/atomic/atoms';
import QuickSearchbarByArray from './QuickSearchbarByArray';
import EllipsisWithTooltip from '@components/atomic/molecules/EllipsisWithTooltip/EllipsisWithTooltip';

const StepTwo = ({
	aggregate,
	setAggregate,
	activeStep,
	clearedProjectsNoPagination,
	isPending,
	selectedProjects,
	selectedEntity,
	setSelectedEntity,
	aggregateTypeChoice,
	setAggregateTypeChoice,
	setSelectedProjects,
	hasAutoSelected,
	setHasAutoSelected,
	isSubstep,
}) => {
	const [allProjects, setAllProjects] = useState([]);
	const [projects, setProjects] = useState([]);
	const [allNotices, setAllNotices] = useState([]);
	const [notices, setNotices] = useState([]);
	const [allBeneficiaries, setAllBeneficiaries] = useState([]);
	const [beneficiaries, setBeneficiaries] = useState([]);
	const [isSearchReset, setIsSearchReset] = useState(false);
	const prevIsSubstep = useRef(isSubstep);

	const aggregateTypes = [
		{
			name: 'cup',
			label: 'Libero (CUP)',
			description: 'Scegli liberamente dalla lista di progetti',
			placeholderText: 'Cerca progetto',
		},
		{
			name: 'notice',
			label: 'Bando',
			description: 'Scegli i progetti associati ad un bando',
			placeholderText: isSubstep ? 'Cerca progetto' : 'Cerca bando',
		},
		{
			name: 'company',
			label: 'Beneficiario',
			description: 'Scegli i progetti associati ad un beneficiario',
			placeholderText: isSubstep ? 'Cerca progetto' : 'Cerca beneficiario',
		},
	];

	useEffect(() => {
		if (!clearedProjectsNoPagination) return;

		const notices = new Map();
		const beneficiaries = new Map();

		clearedProjectsNoPagination.forEach((project) => {
			const { id: noticeId, call: noticeCall, name: noticeName } = project.notice || {};
			if (noticeCall && !notices.has(noticeCall)) {
				notices.set(noticeCall, { noticeId, noticeCall, noticeName });
			}

			const { id: companyId, name: companyName, code: companyCode } = project.company || {};
			if (companyName && !beneficiaries.has(companyName)) {
				beneficiaries.set(companyName, { companyId, companyName, companyCode });
			}
		});

		setAllProjects(clearedProjectsNoPagination);
		setProjects(clearedProjectsNoPagination);

		setAllNotices(Array.from(notices.values()));
		setNotices(Array.from(notices.values()));

		setAllBeneficiaries(Array.from(beneficiaries.values()));
		setBeneficiaries(Array.from(beneficiaries.values()));
	}, [clearedProjectsNoPagination]);

	useEffect(() => {
		if (isSubstep) {
			if (aggregateTypeChoice === 'notice' && selectedEntity) {
				setProjects(allProjects.filter((project) => project.notice && project.notice.id === selectedEntity));
			} else if (aggregateTypeChoice === 'company' && selectedEntity) {
				setProjects(allProjects.filter((project) => project.company && project.company.id === selectedEntity));
			}
		}
	}, [aggregateTypeChoice, allProjects, isSubstep, selectedEntity]);

	useEffect(() => {
		// se isSubstep è passato da false a true oppure da true a false
		if ((!prevIsSubstep.current && isSubstep) || (prevIsSubstep && !isSubstep)) {
			setIsSearchReset(true);
		}
		prevIsSubstep.current = isSubstep;
	}, [isSubstep]);

	// Auto-seleziona i progetti nel substep associati all'entity selezionato
	useEffect(() => {
		if (aggregateTypeChoice !== 'cup' && isSubstep && selectedEntity && !hasAutoSelected) {
			let filteredProjectIds = [];

			if (aggregateTypeChoice === 'notice') {
				filteredProjectIds = allProjects
					.filter((project) => project.notice && project.notice.id === selectedEntity)
					.map((project) => project.id);
			} else if (aggregateTypeChoice === 'company') {
				filteredProjectIds = allProjects
					.filter((project) => project.company && project.company.id === selectedEntity)
					.map((project) => project.id);
			}

			setSelectedProjects(filteredProjectIds);
			setHasAutoSelected(true);
		}
	}, [
		aggregateTypeChoice,
		isSubstep,
		selectedEntity,
		hasAutoSelected,
		setSelectedProjects,
		allProjects,
		setHasAutoSelected,
	]);

	const handleEntitySelect = (e, aggregateTypeChoice, entityId, checked, setSelectedProj) => {
		//console.log('e', e);

		const changeProj = (prev) => {
			let result;

			if (checked) {
				// Aggiungi solo se non già presente
				result = prev.includes(entityId) ? prev : [...prev, entityId];
			} else {
				// Rimuovi se deselezionato
				result = prev.filter((id) => id !== entityId);
			}

			return result;
		};

		if (activeStep === 1 && aggregateTypeChoice === 'cup') {
			setSelectedProj((prev) => {
				let result = changeProj(prev);

				return result;
			});
		} else if (activeStep === 1 && (aggregateTypeChoice === 'notice' || aggregateTypeChoice === 'company')) {
			setSelectedEntity(entityId);
			setHasAutoSelected(false);
		} else if (isSubstep) {
			setSelectedProj((prev) => {
				let result = changeProj(prev);

				return result;
			});
		}
	};

	const aggregateTypeCUP = () => {
		return (
			<div className="test-scroll">
				{projects && projects.length > 0 ? (
					projects.map((project) => (
						<Row key={`project-${project.id}`} className="row-list-entity">
							<Col className="col-auto align-self-center">
								<Form.Check
									type="checkbox"
									id={`project-${project.id}-checkbox`}
									name={`${aggregateTypeChoice}-checklist`}
									checked={selectedProjects.some((id) => id === project.id)}
									onChange={(e) => handleEntitySelect(e, aggregateTypeChoice, project.id, e.target.checked, setSelectedProjects)}
								/>
							</Col>
							<Col>
								<EllipsisWithTooltip text={project.name} className="title-list-entity one-line-ellipsis" />
								<div className="value-list-entity float-start">CUP/ID Progetto&nbsp;</div>
								<EllipsisWithTooltip text={project.cup} className="value-list-entity one-line-ellipsis" />
							</Col>
							<Col>
								<span className="title-list-entity">Costo</span>
								<EllipsisWithTooltip text={formatToDecimals(project.totalAmount) + ' €'} className="value-list-entity one-line-ellipsis" />
							</Col>
							<Col>
								<span className="title-list-entity">Bando</span>
								<EllipsisWithTooltip text={project.notice.call} className="value-list-entity one-line-ellipsis" />
							</Col>
							<Col>
								<span className="title-list-entity">Beneficiario</span>
								<EllipsisWithTooltip text={project.company.name} className="value-list-entity one-line-ellipsis" />
							</Col>
							<Col>
								<span className="title-list-entity">Avvio</span>
								<span className="value-list-entity">{dateFormatter(project.startDate, 'DD/MM/YYYY') ?? 'N.D.'}</span>
							</Col>
						</Row>
					))
				) : (
					<span style={{ textAlign: 'center', padding: '2rem 0', display: 'block' }}>nessun progetto disponibile</span>
				)}
			</div>
		);
	};

	const aggregateTypeNotice = () => {
		return (
			<div className="test-scroll">
				{notices.length > 0 ? (
					notices.map((notice) => (
						<NoticeTile
							key={`notice-${notice.noticeId}`}
							notice={notice}
							aggregateTypeChoice={aggregateTypeChoice}
							selectedEntity={selectedEntity}
							handleEntitySelect={handleEntitySelect}
						/>
					))
				) : (
					<span style={{ textAlign: 'center', padding: '2rem 0', display: 'block' }}>nessun bando disponibile</span>
				)}
			</div>
		);
	};

	const aggregateTypeBeneficiary = () => {
		return (
			<div className="test-scroll">
				{beneficiaries.length > 0 ? (
					<Row className="gx-4 w-100 m-0">
						{beneficiaries.map((beneficiary) => (
							<Col key={`beneficiary-${beneficiary.companyId}`} xs={12} md={4} className="py-3">
								<BeneficiaryTile
									beneficiary={beneficiary}
									aggregateTypeChoice={aggregateTypeChoice}
									selectedEntity={selectedEntity}
									handleEntitySelect={handleEntitySelect}
								/>
							</Col>
						))}
					</Row>
				) : (
					<span style={{ textAlign: 'center', padding: '2rem 0', display: 'block' }}>nessun beneficiario disponibile</span>
				)}
			</div>
		);
	};

	return (
		<>
			{activeStep === 1 || isSubstep ? (
				<>
					<Row className="flex-column content-step-modal">
						{activeStep === 1 && (
							<Col>
								<span className="content-title">Che tipologia di aggregato vuoi creare?</span>
							</Col>
						)}

						<Col>
							<div className="content-form">
								{activeStep === 1 && (
									<Row className="mt-4">
										{aggregateTypes.map((aggregate) => (
											<Col key={aggregate.name} className="col-4">
												<InputGroup className={`aggregate-access-radio-group${aggregateTypeChoice === aggregate.name ? '-checked' : ''}`}>
													<InputGroup.Radio
														className="col-1"
														id={aggregate.name}
														name="aggregateTypeChoice"
														value={aggregate.name}
														checked={aggregateTypeChoice === aggregate.name}
														onChange={(e) => {
															setAggregateTypeChoice(aggregate.name);
															setSelectedProjects([]);
															setSelectedEntity(0);
															setAggregate((prev) => ({ ...prev, notes: aggregate.name }));
															setHasAutoSelected(false);
															setIsSearchReset(true); // resetta la barra di ricerca
														}}
													/>
													<InputGroup.Text className="col-10 flex-column align-items-start text-start p-2" style={{ whiteSpace: 'normal' }}>
														<Row className="d-flex align-items-center">
															<Col className="pe-0 f-500">
																<span style={{ fontSize: '18px', lineHeight: '28px' }}>{aggregate.label}</span>
															</Col>
														</Row>
														<Row>
															<span className="f-400 mt-1" style={{ fontSize: '14px', lineHeight: '24px' }}>
																{aggregate.description}
															</span>
														</Row>
													</InputGroup.Text>
												</InputGroup>
											</Col>
										))}
									</Row>
								)}

								{aggregateTypeChoice && (
									<div className="px-3">
										<Row className="mt-4">
											<Col className="p-0 me-auto">
												<div
													style={{
														backgroundColor: '#fff',
														borderTopLeftRadius: '0.5rem',
														borderTopRightRadius: '0.5rem',
														padding: '1rem',
														justifyItems: 'end',
														display: isSubstep ? 'flex' : 'block',
													}}
												>
													{isSubstep && (
														<div style={{ flex: 7, minWidth: 0 }}>
															<span
																style={{
																	display: 'block',
																	fontSize: '24px',
																	fontWeight: '700',
																	lineHeight: '34px',
																	paddingBottom: '0.5rem',
																}}
															>
																{aggregateTypeChoice === 'notice' &&
																	allNotices.find((notice) => notice.noticeId === selectedEntity)?.noticeCall}
																{aggregateTypeChoice === 'company' &&
																	allBeneficiaries.find((beneficiary) => beneficiary.companyId === selectedEntity)?.companyName}
															</span>
															<span>
																Sono stati selezionati tutti i progetti associati al{' '}
																{aggregateTypes.find((type) => type.name === aggregateTypeChoice)?.label.toLowerCase()}. Deseleziona i
																progetti che vuoi escludere e vai avanti con la configurazione.
															</span>
														</div>
													)}

													<div style={{ flex: 5, minWidth: 0, alignSelf: 'end', justifyItems: 'end' }}>
														<QuickSearchbarByArray
															width="450px"
															allProjects={allProjects}
															allNotices={allNotices}
															allBeneficiaries={allBeneficiaries}
															projects={projects}
															setProjects={setProjects}
															setNotices={setNotices}
															setBeneficiaries={setBeneficiaries}
															aggregateTypeChoice={aggregateTypeChoice}
															placeholderText={
																(aggregateTypes.find((type) => type.name === aggregateTypeChoice)?.placeholderText || '') + '...'
															}
															isSubstep={isSubstep}
															prevIsSubstep={prevIsSubstep.current}
															selectedEntity={selectedEntity}
															isSearchReset={isSearchReset}
															setIsSearchReset={setIsSearchReset}
														/>
													</div>
												</div>
											</Col>
										</Row>

										<Row className="flex-column">
											<Col className="col-list-entity">
												{isPending ? (
													<div className="py-5">
														<Loading />
													</div>
												) : (
													<>
														{aggregateTypeChoice === 'cup' || isSubstep ? aggregateTypeCUP() : null}
														{aggregateTypeChoice === 'notice' && !isSubstep ? aggregateTypeNotice() : null}
														{aggregateTypeChoice === 'company' && !isSubstep ? aggregateTypeBeneficiary() : null}
													</>
												)}
											</Col>
										</Row>
									</div>
								)}
							</div>
						</Col>
					</Row>
				</>
			) : null}
		</>
	);
};
export default StepTwo;
